import {
  ClockIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  StarIcon
} from '@heroicons/react/16/solid'
import {
  AcademicCapIcon,
  ArrowsRightLeftIcon,
  BookOpenIcon,
  BriefcaseIcon,
  InformationCircleIcon,
  RocketLaunchIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import { motion, useReducedMotion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tooltip'

import aivyLogo from '../assets/images/aivy_logo_rgb.png'
import trackEvents from '../constants/track-events'
import {
  activeAssignments,
  buildContext,
  exposeFrozen,
  freezeSurface
} from '../features/experiments'
import { mutation, query } from '../graphql'
import { trackEvent } from '../helpers/analytics'
import free_email_provider_set from '../helpers/companyEmailValidator/free_email_provider_domains'

// ─── Score-Modell (gem. Briefing) ─────────────────────────────────────────────

const JOB_TYPE_INFO_MAP = {
  'Ausbildung & Duales Studium': { enum: 'LOW_INFO', points: 100 },
  'Praktikum, Werkstudium & Trainee': { enum: 'LOW_INFO', points: 100 },
  Quereinstieg: { enum: 'LOW_INFO', points: 100 },
  'Berufseinstieg & Junior-Positionen': { enum: 'MIXED', points: 50 },
  'Fachkräfte mit Berufserfahrung': { enum: 'MIXED', points: 50 },
  'Führungskräfte & Management': { enum: 'HIGH_INFO', points: 0 }
}
const JOB_TYPES = Object.keys(JOB_TYPE_INFO_MAP)

function infoDichteFromJobs(jobs) {
  if (!jobs.length) return { enum: 'MIXED', points: 50 }
  return jobs
    .map((j) => JOB_TYPE_INFO_MAP[j])
    .reduce((max, cur) => (cur.points > max.points ? cur : max), {
      enum: 'HIGH_INFO',
      points: 0
    })
}

const VOLUME_MAP = {
  'sehr-viele': { enum: 'VERY_HIGH', points: 100 },
  viele: { enum: 'HIGH', points: 80 },
  wenige: { enum: 'MEDIUM', points: 50 },
  'sehr-wenige': { enum: 'LOW', points: 0 }
}

const HIRING_PLAN_MAP = {
  'unter-10': 'UNDER_10',
  '10-50': 'TEN_TO_FIFTY',
  'mehr-als-50': 'OVER_50'
}

const ATS_MAP = {
  ja: { enum: 'JA', points: 100 },
  nein: { enum: 'NEIN', points: 0 },
  'weiss-nicht': { enum: 'UNBEKANNT', points: 0 }
}

// Option-IDs je Frage (Modul-Ebene — auch vom Keyboard-Handler genutzt).
// Reihenfolge muss zur i18n-Options-Reihenfolge passen.
const VOLUME_IDS = ['sehr-viele', 'viele', 'wenige', 'sehr-wenige']
const HIRING_IDS = ['unter-10', '10-50', 'mehr-als-50']
const ATS_IDS = ['ja', 'nein', 'weiss-nicht']

function groesseFromEmployees(n) {
  if (n == null) return { enum: 'UNKNOWN', points: 25 }
  if (n > 1000) return { enum: 'ENTERPRISE', points: 100 }
  if (n > 50) return { enum: 'KMU', points: 50 }
  return { enum: 'STARTUP_KLEIN', points: 0 }
}

function calcLeadScore({ volume, info, ats, groesse }) {
  return Math.round(
    0.35 * volume.points +
      0.25 * info.points +
      0.2 * ats.points +
      0.2 * groesse.points
  )
}

function dimFlag(score, invertedIdeal = false) {
  const effective = invertedIdeal ? 100 - score : score
  if (effective >= 65) return 'green'
  if (effective <= 35) return 'red'
  return 'yellow'
}

const DIM_COLORS = {
  volume: { green: '#29CCC7', yellow: '#FFB629', red: '#FF6B8A' },
  density: { green: '#29CCC7', yellow: '#FFB629', red: '#FF6B8A' }
}
const DIM_ICONS = {
  volume: { green: '⚡', yellow: '!', red: '!' },
  density: { green: '⚡', yellow: '!', red: '!' }
}
const TRIAL_ICONS = { green: '→', yellow: '→', red: '→' }
const TRIAL_COLORS = { green: '#29CCC7', yellow: '#FFB629', red: '#8FA8C0' }

const PRIVATE_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.de',
  'yahoo.co.uk',
  'yahoo.fr',
  'hotmail.com',
  'hotmail.de',
  'hotmail.co.uk',
  'hotmail.fr',
  'outlook.com',
  'outlook.de',
  'live.com',
  'live.de',
  'msn.com',
  'web.de',
  'gmx.de',
  'gmx.com',
  'gmx.net',
  'gmx.at',
  'gmx.ch',
  't-online.de',
  'freenet.de',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'ymail.com',
  'protonmail.com',
  'proton.me',
  'pm.me',
  'mailbox.org',
  'tutanota.com',
  'posteo.de',
  'zoho.com',
  'yandex.com',
  'yandex.ru'
])

// Häufige Tippfehler-Varianten von Freemail-Domains
const PRIVATE_DOMAIN_TYPOS = new Set([
  'gmil.com',
  'gmial.com',
  'gmaill.com',
  'gmali.com',
  'gnail.com',
  'gmail.de',
  'gmail.con',
  'gmail.cm',
  'gmial.de',
  'gmil.de',
  'hotnail.com',
  'hotmial.com',
  'hotmil.com',
  'hotmaill.com',
  'yaho.com',
  'yhoo.com',
  'outlok.com',
  'outlook.con',
  'gmx.de.com',
  'web.de.com',
  'email.de'
])

// Subdomain-Präfixe, die auf Studierende hindeuten (keine Business-Mail)
const STUDENT_PREFIXES = [
  'stud.',
  'student.',
  'studenten.',
  'students.',
  'stud-',
  'studi.'
]

function isPrivateEmail(email) {
  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain) return false
  if (PRIVATE_DOMAINS.has(domain)) return true
  if (PRIVATE_DOMAIN_TYPOS.has(domain)) return true
  if (STUDENT_PREFIXES.some((p) => domain.startsWith(p))) return true
  // Große Blocklist (Freemailer + Wegwerf-Domains, ~16k Einträge) — die
  // handgepflegte PRIVATE_DOMAINS-Liste oben fing nur die Top-Freemailer;
  // mail.ru, ukr.net, tempemail.de & Co. rutschten durch (Befund 2026-07-10).
  if (free_email_provider_set.has(domain)) return true
  return false
}

const isValidEmailFormat = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

// Klassifiziert, warum eine E-Mail an der WELCOME-Hürde geblockt wird.
// null = zulässige Business-Mail. Sonst der Grund — damit wir im Item sehen,
// ob Abbrecher an Format / Freemail / Freemail-Typo / Studenten-Mail scheitern.
function classifyEmailBlock(email) {
  if (!isValidEmailFormat(email)) return 'INVALID_FORMAT'
  const domain = email.split('@')[1]?.toLowerCase() || ''
  if (PRIVATE_DOMAINS.has(domain)) return 'FREEMAIL'
  if (PRIVATE_DOMAIN_TYPOS.has(domain)) return 'FREEMAIL_TYPO'
  if (STUDENT_PREFIXES.some((p) => domain.startsWith(p))) return 'STUDENT'
  // Große Blocklist als letzte Stufe (gleicher reason wie FREEMAIL, damit
  // bestehende Auswertungen auf data.blocked_attempts.reason stabil bleiben)
  if (free_email_provider_set.has(domain)) return 'FREEMAIL'
  return null
}

// ─── Funnel-Session (Dedup gegen Modal-Remounts/Reopens) ─────────────────────
// localStorage (nicht sessionStorage!) — überlebt das iframe-Neuaufbauen beim
// Modal-Schließen/Öffnen. TTL hält Reuse auf eine laufende Session begrenzt,
// completed=true verhindert Reuse nach Abschluss → echter Neustart legt neu an.
const FUNNEL_SESSION_KEY = 'funnel_session'
const FUNNEL_SESSION_TTL_MS = 30 * 60 * 1000

const genId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`

function readFunnelSession() {
  try {
    const s = JSON.parse(localStorage.getItem(FUNNEL_SESSION_KEY) || 'null')
    if (!s || s.completed) return null
    if (
      typeof s.created_at !== 'number' ||
      Date.now() - s.created_at > FUNNEL_SESSION_TTL_MS
    )
      return null
    return s
  } catch (_) {
    return null
  }
}

function writeFunnelSession(patch) {
  try {
    const cur =
      JSON.parse(localStorage.getItem(FUNNEL_SESSION_KEY) || '{}') || {}
    localStorage.setItem(
      FUNNEL_SESSION_KEY,
      JSON.stringify({ ...cur, ...patch })
    )
  } catch (_) {
    /* Storage im 3rd-party-iframe blockiert → Fallback: kein Reuse, neues Item */
  }
}

// ─── Steps ───────────────────────────────────────────────────────────────────
// 0 WELCOME · 1 CREATING · 2 Q1 (jobs) · 3 Q2 (volume) · 4 Q3 (hiring) · 5 Q4 (ats) · 6 DONE
const STEPS = {
  WELCOME: 0,
  CREATING: 1,
  Q1: 2,
  Q2: 3,
  Q3: 4,
  Q4: 5,
  DONE: 6
}

// ─── SVG icons ───────────────────────────────────────────────────────────────
const CloseIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
  >
    <path d='M6 6l12 12M18 6l-12 12' />
  </svg>
)
const ArrowRight = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M5 12h14M13 5l7 7-7 7' />
  </svg>
)
const ArrowLeft = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M19 12H5M11 19l-7-7 7-7' />
  </svg>
)
const CheckMark = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='3'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M5 13l4 4L19 7' />
  </svg>
)
const MailIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke={color}
    strokeWidth='2'
  >
    <rect x='3' y='5' width='18' height='14' rx='2' />
    <path d='M3 7l9 6 9-6' />
  </svg>
)
// Nur der Anchor — die Tooltip-Instanz selbst hängt einmalig am Backdrop
// (siehe FunnelQ4). react-tooltip mountet ihren Knoten erst beim Öffnen und
// entfernt ihn beim Schließen wieder; stünde sie hier zwischen <h2> und
// Options-Grid, würde `.lm-q-title ~ .lm-options-grid` beim Öffnen greifen und
// der Abstand um 16px springen. Kein data-tooltip-place hier: Anchor-Attribute
// überschreiben das place-Prop und würden das responsive Placement aushebeln.
function QuestionTitle({ titleNode, why, titleRef }) {
  return (
    <h2 className='lm-q-title' ref={titleRef} tabIndex={-1}>
      {titleNode}
      {why && (
        <button
          type='button'
          className='lm-q-info'
          data-tooltip-id='funnel-q-why'
          data-tooltip-content={why}
          aria-label='Info'
        >
          <InformationCircleIcon aria-hidden='true' />
        </button>
      )}
    </h2>
  )
}

// Schmale Viewports brauchen ein anderes Tooltip-Verhalten: rechts vom Info-Icon
// ist kein Platz für 260px, und Hover existiert auf Touch nicht.
const NARROW_QUERY = '(max-width: 640px)'

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(NARROW_QUERY).matches
  )
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    )
      return
    const mq = window.matchMedia(NARROW_QUERY)
    const onChange = (e) => setIsNarrow(e.matches)
    setIsNarrow(mq.matches)
    // addEventListener auf MediaQueryList erst ab Safari 14 — addListener als Fallback.
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }
    mq.addListener(onChange)
    return () => mq.removeListener(onChange)
  }, [])
  return isNarrow
}

// ─── Progress Bar (visible during Q1–Q4) ─────────────────────────────────────
function ProgressBar({ step, t }) {
  if (step < STEPS.Q1 || step > STEPS.Q4) return null
  const visibleStep = step - STEPS.Q1 + 1
  return (
    <div className='lm-modal-progress'>
      <div className='lm-pd'>
        <div
          className='lm-pd-fill'
          style={{ width: visibleStep >= 1 ? '100%' : '0%' }}
        />
      </div>
      <div className='lm-pd'>
        <div
          className='lm-pd-fill'
          style={{ width: visibleStep >= 2 ? '100%' : '0%' }}
        />
      </div>
      <div className='lm-pd'>
        <div
          className='lm-pd-fill'
          style={{ width: visibleStep >= 3 ? '100%' : '0%' }}
        />
      </div>
      <div className='lm-pd'>
        <div
          className='lm-pd-fill'
          style={{ width: visibleStep >= 4 ? '100%' : '0%' }}
        />
      </div>
      <div className='lm-modal-progress-label'>
        {t('funnel_q4.progress_label', { current: visibleStep, total: 4 })}
      </div>
    </div>
  )
}

function BuildingStrip({ t, step }) {
  const remaining = step >= STEPS.Q1 ? STEPS.Q4 - step + 1 : 4
  return (
    <div className='lm-building-strip'>
      <div className='lm-creating-mini-spin' />
      <span>{t('funnel_q4.building_strip', { count: remaining })}</span>
    </div>
  )
}

// ─── Welcome (Email-first) ───────────────────────────────────────────────────
function WelcomeStep({
  email,
  emailFeedback,
  blockReason,
  showTalentNotice,
  onEmailChange,
  onEmailBlur,
  onSubmit,
  inputRef,
  onTalentHintClick,
  t,
  transComponents
}) {
  const reduceMotion = useReducedMotion()
  // Enter-Animation überspringen und direkt im Endzustand rendern, wenn sie
  // nicht laufen kann oder nicht laufen soll:
  //   - reduzierte Bewegung ist gewünscht
  //   - der Tab ist im Hintergrund → kein requestAnimationFrame, framer-motion
  //     bliebe beim initial-State (opacity 0) hängen und die Meldung wäre
  //     unsichtbar. Eine Meldung, die den Weiter-Button blockt, darf nicht von
  //     einem Animation-Frame abhängen.
  const skipEnterAnimation =
    reduceMotion ||
    (typeof document !== 'undefined' && document.visibilityState === 'hidden')
  // Fehler nur zeigen, wenn der Nutzer wirklich etwas eingegeben hat (LIVE) oder
  // leer abschicken wollte (REQUIRED) — ein leeres, unberührtes Feld ist kein Fehler.
  const value = email.trim()
  const showRequired = emailFeedback === 'REQUIRED' && !value
  const showInvalid =
    emailFeedback === 'LIVE' && !!value && blockReason === 'INVALID_FORMAT'
  // Rot nur für echte Eingabefehler (leer, kaputtes Format). Die Talent-Weiche
  // ist kein Fehler: die Adresse ist gültig, nur die Zielgruppe stimmt nicht.
  const hasError = showRequired || showInvalid
  return (
    <>
      <h2 className='lm-step-title lm-step-title--sm'>
        <Trans i18nKey='funnel_q4.welcome.title' components={transComponents} />
      </h2>
      <div className='lm-welcome-input-wrap'>
        <label htmlFor='funnel-email' className='lm-welcome-input-label'>
          {t('funnel_q4.welcome.email_label')}
        </label>
        <div
          className={`lm-welcome-input-inner ${
            hasError ? 'is-error' : showTalentNotice ? 'is-notice' : ''
          }`}
        >
          <MailIcon color='var(--cadet-blue)' />
          <input
            ref={inputRef}
            id='funnel-email'
            type='email'
            className='lm-welcome-input'
            placeholder={t('funnel_q4.welcome.email_placeholder')}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            onBlur={onEmailBlur}
            onKeyDown={(e) => {
              // Gehaltenes Enter würde pro Repeat einen blocked_attempts-Eintrag
              // und eine updateForm-Mutation feuern.
              if (e.repeat) return
              // Immer submitten — handleEmailSubmit klassifiziert selbst und
              // erfasst geblockte Versuche, statt sie stillschweigend zu schlucken.
              if (e.key === 'Enter') onSubmit()
            }}
            aria-invalid={hasError || showTalentNotice}
            aria-required='true'
            aria-describedby='funnel-email-message'
            autoFocus
          />
          {/* Slot bleibt stehen: unter LIVE kippt die Gültigkeit beim Tippen —
              würde das Icon aus-/einhängen, springt der Caret im Flex-Row. */}
          {showTalentNotice ? (
            <ExclamationTriangleIcon
              className='lm-input-notice-icon'
              aria-hidden='true'
            />
          ) : (
            <ExclamationCircleIcon
              className={`lm-input-error-icon ${hasError ? '' : 'is-hidden'}`}
              aria-hidden='true'
            />
          )}
        </div>
        {/* Eine stabile Zeile für Fehler, Helper und Talent-Weiche — kein
            Layout-Shift, und die Live-Region existiert im DOM, bevor Text
            hineinkommt (sonst stumm). Die min-height in der CSS reserviert den
            längsten Zustand, damit der Wechsel die zentrierte Karte nicht
            verschiebt (siehe .lm-welcome-message). */}
        <p
          id='funnel-email-message'
          className={`lm-welcome-message ${
            hasError
              ? 'lm-welcome-error'
              : showTalentNotice
                ? 'lm-welcome-notice'
                : 'lm-welcome-helper'
          }`}
          aria-live='polite'
        >
          {showRequired && t('funnel_q4.welcome.error_required')}
          {showInvalid && t('funnel_q4.welcome.error_invalid')}
          {/* B2C-Abzweig: erscheint erst, wenn wir eine Freemail-/Studenten-
              Adresse erkennen. Erklärt den Block und zeigt den richtigen Weg
              (aivy.app/talente), statt nur "bitte geschäftliche Mail". */}
          {showTalentNotice && (
            <motion.span
              className='lm-welcome-notice-inner'
              initial={skipEnterAnimation ? false : { opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            >
              <ExclamationTriangleIcon aria-hidden='true' />
              <span>
                {/* Der Pfeil hängt als ::after am Link (CSS) — Trans ersetzt die
                    Children der components-Elemente durch den übersetzten Text,
                    ein Icon als Child würde also verworfen. */}
                <Trans
                  i18nKey='funnel_q4.welcome.talent_notice'
                  components={{
                    talentLink: (
                      <a
                        href='https://www.aivy.app/talente'
                        target='_blank'
                        rel='noreferrer noopener'
                        onClick={onTalentHintClick}
                      />
                    )
                  }}
                />
                <span className='lm-sr-only'>
                  {' '}
                  ({t('funnel_q4.welcome.talent_notice_new_tab')})
                </span>
              </span>
            </motion.span>
          )}
          {!hasError && !showTalentNotice && (
            <>
              <CheckMark size={12} />
              {t('funnel_q4.welcome.helper')}
            </>
          )}
        </p>
      </div>
      <p className='lm-welcome-terms'>
        <Trans
          i18nKey='funnel_q4.welcome.terms_notice'
          components={[
            <a
              key='terms'
              href='https://www.aivy.app/agb'
              target='_blank'
              rel='noreferrer noopener'
            />,
            <a
              key='privacy'
              href='https://www.aivy.app/datenschutz'
              target='_blank'
              rel='noreferrer noopener'
            />
          ]}
        />
      </p>
    </>
  )
}

// ─── Creating (Phase 0 spinner → Phase 1 handoff) ────────────────────────────
function CreatingStep({ phase, t }) {
  if (phase === 0) {
    return (
      <div className='lm-starting'>
        <div className='lm-starting-orbit'>
          <div className='lm-starting-core' />
          <div className='lm-starting-ring' />
          <div className='lm-starting-ring lm-starting-ring-2' />
        </div>
        <h2 className='lm-starting-title'>
          {t('funnel_q4.creating.starting_title')}
        </h2>
        <p className='lm-starting-sub'>
          {t('funnel_q4.creating.starting_sub')}
        </p>
      </div>
    )
  }
  return (
    <div className='lm-creating-handoff'>
      <h2 className='lm-step-title'>{t('funnel_q4.creating.handoff_title')}</h2>
      <p className='lm-step-lead'>{t('funnel_q4.creating.handoff_lead')}</p>
      <ul className='lm-handoff-benefits'>
        <li>
          <span className='lm-handoff-benefit-check'>
            <CheckMark size={12} />
          </span>
          {t('funnel_q4.creating.handoff_benefit_1')}
        </li>
        <li>
          <span className='lm-handoff-benefit-check'>
            <CheckMark size={12} />
          </span>
          {t('funnel_q4.creating.handoff_benefit_2')}
        </li>
      </ul>
      <div className='lm-creating-meta'>
        <span className='lm-creating-meta-chip'>
          <svg
            width='12'
            height='12'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          >
            <circle cx='12' cy='12' r='9' />
            <path d='M12 7v5l3 2' />
          </svg>
          {t('funnel_q4.creating.handoff_chip')}
        </span>
      </div>
    </div>
  )
}

// ─── Option Grid ─────────────────────────────────────────────────────────────
function OptionGrid({ options, value, onChange, multi, columns = 2, variant }) {
  const isSelected = (id) => (multi ? value.includes(id) : value === id)
  const tile = variant === 'tile'
  const toggle = (id) => {
    if (multi) {
      onChange(
        value.includes(id) ? value.filter((v) => v !== id) : [...value, id]
      )
    } else {
      onChange(id)
    }
  }
  return (
    <div
      className={`lm-options-grid ${columns === 1 ? 'single' : ''} ${tile ? 'tiles' : ''}`}
      role={multi ? 'group' : 'radiogroup'}
    >
      {options.map((o) => (
        <button
          key={o.id}
          type='button'
          role={multi ? 'checkbox' : 'radio'}
          aria-checked={isSelected(o.id)}
          className={`lm-option ${o.desc ? 'lm-option--stacked' : ''} ${isSelected(o.id) ? 'selected' : ''}`}
          onClick={() => toggle(o.id)}
        >
          <span className={`lm-option-check ${multi ? '' : 'radio'}`}>
            {isSelected(o.id) && <CheckMark size={12} />}
          </span>
          {o.icon && <span className='lm-option-icon'>{o.icon}</span>}
          <span className='lm-option-textcol'>
            <span className='lm-option-label'>{o.label}</span>
            {o.desc && <span className='lm-option-desc'>{o.desc}</span>}
          </span>
        </button>
      ))}
    </div>
  )
}

// ─── Question Step ───────────────────────────────────────────────────────────
// HeroIcons je Job-Typ (statt Emojis) — semantisch gemappt, Reihenfolge = JOB_TYPES
const Q1_ICONS = [
  AcademicCapIcon,
  BookOpenIcon,
  ArrowsRightLeftIcon,
  RocketLaunchIcon,
  BriefcaseIcon,
  UserGroupIcon
]

function QuestionStep({ step, answers, setAnswers, t, transComponents }) {
  // a11y: bei jedem Schrittwechsel Fokus auf die Frage → Screenreader liest sie
  // vor, Keyboard-Navigation startet am Anfang der Frage.
  const titleRef = useRef(null)
  useEffect(() => {
    titleRef.current?.focus()
  }, [step])

  if (step === STEPS.Q1) {
    const labels = t('funnel_q4.step0.job_type_labels', { returnObjects: true })
    const options = JOB_TYPES.map((id, i) => {
      const Icon = Q1_ICONS[i]
      return { id, label: labels[i] ?? id, icon: <Icon aria-hidden='true' /> }
    })
    return (
      <>
        <QuestionTitle
          titleRef={titleRef}
          titleNode={
            <Trans
              i18nKey='funnel_q4.step0.question'
              components={transComponents}
            />
          }
          why={t('funnel_q4.step0.why')}
        />
        <p className='lm-q-sub'>{t('funnel_q4.step0.hint')}</p>
        <OptionGrid
          options={options}
          value={answers.jobTypes}
          onChange={(v) => setAnswers((a) => ({ ...a, jobTypes: v }))}
          multi
          variant='tile'
        />
      </>
    )
  }
  if (step === STEPS.Q2) {
    const opts = t('funnel_q4.step1.options', { returnObjects: true })
    const options = VOLUME_IDS.map((id, i) => ({
      id,
      label: opts[i]?.label,
      desc: opts[i]?.sub
    }))
    return (
      <>
        <QuestionTitle
          titleRef={titleRef}
          titleNode={
            <Trans
              i18nKey='funnel_q4.step1.question'
              components={transComponents}
            />
          }
          why={t('funnel_q4.step1.why')}
        />
        <p className='lm-q-sub'>{t('funnel_q4.step1.hint')}</p>
        <OptionGrid
          options={options}
          value={answers.volume}
          onChange={(v) => setAnswers((a) => ({ ...a, volume: v }))}
          columns={1}
        />
      </>
    )
  }
  if (step === STEPS.Q3) {
    const opts = t('funnel_q4.step2.options', { returnObjects: true })
    const options = HIRING_IDS.map((id, i) => ({ id, label: opts[i]?.label }))
    return (
      <>
        <h2 className='lm-q-title' ref={titleRef} tabIndex={-1}>
          <Trans
            i18nKey='funnel_q4.step2.question'
            components={transComponents}
          />
        </h2>
        <OptionGrid
          options={options}
          value={answers.hiringPlan}
          onChange={(v) => setAnswers((a) => ({ ...a, hiringPlan: v }))}
          columns={1}
        />
      </>
    )
  }
  if (step === STEPS.Q4) {
    const opts = t('funnel_q4.step3.options', { returnObjects: true })
    const options = ATS_IDS.map((id, i) => ({ id, label: opts[i]?.label }))
    return (
      <>
        <QuestionTitle
          titleRef={titleRef}
          titleNode={
            <Trans
              i18nKey='funnel_q4.step3.question'
              components={transComponents}
            />
          }
          why={t('funnel_q4.step3.why')}
        />
        <OptionGrid
          options={options}
          value={answers.ats}
          onChange={(v) => setAnswers((a) => ({ ...a, ats: v }))}
          columns={1}
        />
      </>
    )
  }
  return null
}

// ─── Passungsprofil (preserved from current funnel) ──────────────────────────
function Passungsprofil({ answers, brandData, t }) {
  const dim1Score = answers.volume ? VOLUME_MAP[answers.volume].points : 0
  const dim2Score = useMemo(() => {
    if (!answers.jobTypes.length) return 50
    const info = infoDichteFromJobs(answers.jobTypes)
    if (info.enum === 'LOW_INFO') return 15
    if (info.enum === 'MIXED') return 50
    return 85
  }, [answers.jobTypes])

  const components = {
    volume: answers.volume
      ? VOLUME_MAP[answers.volume]
      : { enum: 'LOW', points: 0 },
    info: infoDichteFromJobs(answers.jobTypes),
    ats: answers.ats ? ATS_MAP[answers.ats] : { enum: 'UNBEKANNT', points: 0 },
    groesse: groesseFromEmployees(brandData?.employees)
  }
  const matchScore = calcLeadScore(components)
  const flag = matchScore >= 65 ? 'green' : matchScore >= 40 ? 'yellow' : 'red'

  const scoreColor =
    flag === 'green' ? '#29CCC7' : flag === 'yellow' ? '#FFB629' : '#FF6B8A'
  const markerColor = (f) =>
    f === 'green' ? '#29CCC7' : f === 'yellow' ? '#FFB629' : '#FF6B8A'

  const f1 = dimFlag(dim1Score)
  const f2 = dimFlag(dim2Score, true)

  return (
    <div className='q4-result-card lm-result-card'>
      <div className='q4-result-card-header'>
        <div className='q4-result-card-title'>
          <span>{t('funnel_q4.step5.result_title')}</span>
          {brandData?.name && (
            <span className='q4-result-card-sub'>Aivy × {brandData.name}</span>
          )}
        </div>
        <div className='q4-total-score'>
          <div className='q4-total-num' style={{ color: scoreColor }}>
            {matchScore}
          </div>
          <div className='q4-total-bar'>
            <div
              className='q4-total-bar-fill'
              style={{ width: `${matchScore}%`, background: scoreColor }}
            />
          </div>
          <div className='q4-total-label'>
            {t('funnel_q4.step5.score_label')}
          </div>
        </div>
      </div>

      <div className='q4-dim'>
        <span className='q4-dim-label'>{t('funnel_q4.step5.dim1_label')}</span>
        <div className='q4-score-bar-outer'>
          <div className='q4-score-bar-track'>
            <div className='q4-score-zone q4-score-zone-gray' />
            <div className='q4-score-zone q4-score-zone-yellow' />
            <div className='q4-score-zone q4-score-zone-teal' />
          </div>
          <div
            className='q4-score-marker'
            style={{ left: `${dim1Score}%`, background: markerColor(f1) }}
          >
            {f1 === 'red' ? '×' : f1 === 'yellow' ? '!' : '✓'}
          </div>
        </div>
        <div className='q4-dim-ends'>
          <span>{t('funnel_q4.step5.scale_low')}</span>
          <span>{t('funnel_q4.step5.scale_high')}</span>
        </div>
        <div className='q4-dim-insight-box'>
          <div className='q4-dim-section-label'>
            {t(`funnel_q4.step5.flag_label.${f1}`)}
          </div>
          <p className='q4-dim-insight'>
            <span
              className='q4-dim-insight-icon'
              style={{ background: DIM_COLORS.volume[f1] }}
            >
              {DIM_ICONS.volume[f1]}
            </span>
            {t(`funnel_q4.step5.dim_copy.volume.${f1}`)}
          </p>
        </div>
      </div>

      <div className='q4-dim'>
        <span className='q4-dim-label'>{t('funnel_q4.step5.dim2_label')}</span>
        <div className='q4-score-bar-outer'>
          <div className='q4-score-bar-track'>
            <div className='q4-score-zone q4-score-zone-teal' />
            <div className='q4-score-zone q4-score-zone-yellow' />
            <div className='q4-score-zone q4-score-zone-gray' />
          </div>
          <div
            className='q4-score-marker'
            style={{ left: `${dim2Score}%`, background: markerColor(f2) }}
          >
            {f2 === 'red' ? '×' : f2 === 'yellow' ? '!' : '✓'}
          </div>
        </div>
        <div className='q4-dim-ends'>
          <span>{t('funnel_q4.step5.scale_low')}</span>
          <span>{t('funnel_q4.step5.scale_high')}</span>
        </div>
        <div className='q4-dim-insight-box'>
          <div className='q4-dim-section-label'>
            {t(`funnel_q4.step5.flag_label.${f2}`)}
          </div>
          <p className='q4-dim-insight'>
            <span
              className='q4-dim-insight-icon'
              style={{ background: DIM_COLORS.density[f2] }}
            >
              {DIM_ICONS.density[f2]}
            </span>
            {t(`funnel_q4.step5.dim_copy.density.${f2}`)}
          </p>
        </div>
      </div>

      <div
        className='q4-trial-section'
        style={{
          '--trial-accent': scoreColor,
          '--trial-bg': `color-mix(in srgb, ${scoreColor} 14%, transparent)`
        }}
      >
        <div className='q4-dim-section-label'>
          {t('funnel_q4.step5.trial_label')}
        </div>
        <p className='q4-dim-insight'>
          <span
            className='q4-dim-insight-icon'
            style={{ background: TRIAL_COLORS[flag] }}
          >
            {TRIAL_ICONS[flag]}
          </span>
          {t(`funnel_q4.step5.trial_copy.${flag}`)}
        </p>
      </div>
    </div>
  )
}

// ─── Done Step ───────────────────────────────────────────────────────────────
function DashboardReadyBanner({ t }) {
  return (
    <div className='lm-ready-banner'>
      <div className='lm-ready-banner-icon'>
        <span className='lm-ready-ring' />
        <span className='lm-ready-ring lm-ready-ring-2' />
        <span className='lm-ready-check'>
          <CheckMark size={22} />
        </span>
        <span className='lm-ready-spark lm-ready-spark-1' />
        <span className='lm-ready-spark lm-ready-spark-2' />
        <span className='lm-ready-spark lm-ready-spark-3' />
        <span className='lm-ready-spark lm-ready-spark-4' />
      </div>
      <div className='lm-ready-banner-text'>
        <div className='lm-ready-banner-title'>
          {t('funnel_q4.done.ready_title')}
        </div>
        <div className='lm-ready-banner-sub'>
          {t('funnel_q4.done.ready_sub')}
        </div>
      </div>
    </div>
  )
}

// Scoring-Checklist während des Countdowns. Statt "Dashboard wird gebaut"
// (klingt nach Wartezeit) zeigen wir die 4 Score-Faktoren mit ihren
// Gewichtungen und den live errechneten Punkte-Beiträgen. Lehrreich +
// reframt die Wartezeit als sinnvolle Arbeit.
function ScoringChecklist({
  elapsedSeconds,
  totalSeconds,
  answers,
  brandData,
  t
}) {
  // Score-Komponenten aus den User-Angaben
  const volume = answers.volume
    ? VOLUME_MAP[answers.volume]
    : { enum: 'LOW', points: 0 }
  const info = infoDichteFromJobs(answers.jobTypes)
  const atsData = answers.ats
    ? ATS_MAP[answers.ats]
    : { enum: 'UNBEKANNT', points: 0 }
  const groesse = groesseFromEmployees(brandData?.employees)

  const items = [
    {
      key: 'volume',
      weight: 35,
      contribution: Math.round(0.35 * volume.points),
      completeAt: Math.round(totalSeconds * 0.22)
    },
    {
      key: 'info',
      weight: 25,
      contribution: Math.round(0.25 * info.points),
      completeAt: Math.round(totalSeconds * 0.46)
    },
    {
      key: 'ats',
      weight: 20,
      contribution: Math.round(0.2 * atsData.points),
      completeAt: Math.round(totalSeconds * 0.72)
    },
    {
      key: 'company',
      weight: 20,
      contribution: Math.round(0.2 * groesse.points),
      completeAt: Math.round(totalSeconds * 0.93)
    }
  ]
  const runningTotal = items.reduce(
    (sum, item) =>
      elapsedSeconds >= item.completeAt ? sum + item.contribution : sum,
    0
  )
  return (
    <div className='lm-setup-card'>
      <div className='lm-setup-card-header'>
        <div className='lm-setup-spinner'>
          <span className='lm-setup-spinner-core' />
          <span className='lm-setup-spinner-ring' />
        </div>
        <div className='lm-setup-card-headtext'>
          <div className='lm-setup-card-title'>
            {t('funnel_q4.done.scoring_title')}
          </div>
          <div className='lm-setup-card-sub'>
            {t('funnel_q4.done.scoring_sub')}
          </div>
        </div>
        <div className='lm-setup-card-running'>
          <div className='lm-setup-running-num'>{runningTotal}</div>
          <div className='lm-setup-running-label'>
            {t('funnel_q4.done.scoring_running_label')}
          </div>
        </div>
      </div>
      <ul className='lm-setup-list'>
        {items.map((item, i) => {
          const prevDone = i === 0 ? 0 : items[i - 1].completeAt
          const isDone = elapsedSeconds >= item.completeAt
          const isActive = !isDone && elapsedSeconds >= prevDone
          const cls = isDone ? 'is-done' : isActive ? 'is-active' : 'is-pending'
          return (
            <li
              key={item.key}
              className={`lm-setup-item lm-setup-item--scoring ${cls}`}
            >
              <span className='lm-setup-item-marker'>
                {isDone ? (
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='3'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M5 13l4 4L19 7' />
                  </svg>
                ) : isActive ? (
                  <span className='lm-setup-item-spin' />
                ) : (
                  <span className='lm-setup-item-dot' />
                )}
              </span>
              <span className='lm-setup-item-label'>
                {t(`funnel_q4.done.scoring_steps.${item.key}`)}
              </span>
              <span className='lm-setup-item-weight'>{item.weight}%</span>
              <span className='lm-setup-item-contribution'>
                {isDone ? `+${item.contribution}` : isActive ? '…' : ''}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function DoneStep({
  answers,
  brandData,
  t,
  doneCountdown,
  totalSeconds,
  ctaUnlocked,
  revealProfile
}) {
  // Bei früher Freigabe die Checklist auf den Endzustand ziehen: der Score ist
  // fertig, nur die zeitgesteuerte Animation wäre noch mitten im Lauf.
  const elapsed = ctaUnlocked ? totalSeconds : totalSeconds - doneCountdown
  return (
    <div className='lm-done'>
      {!revealProfile ? (
        <ScoringChecklist
          elapsedSeconds={elapsed}
          totalSeconds={totalSeconds}
          answers={answers}
          brandData={brandData}
          t={t}
        />
      ) : (
        <>
          <DashboardReadyBanner t={t} />
          <div className='lm-done-section-label'>
            {t('funnel_q4.done.profile_section')}
          </div>
          <Passungsprofil answers={answers} brandData={brandData} t={t} />
        </>
      )}
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function FunnelQ4({ onClose }) {
  const { t, i18n } = useTranslation()
  const isNarrow = useIsNarrow()
  const transComponents = {
    u: <span className='lm-u' />,
    br: <br />,
    strong: <strong />
  }

  const [step, setStep] = useState(STEPS.WELCOME)
  const [email, setEmail] = useState(() => {
    try {
      const raw = sessionStorage.getItem('funnel_q4_email')
      if (!raw) return ''
      const { value, expiresAt } = JSON.parse(raw)
      if (typeof expiresAt === 'number' && expiresAt > Date.now())
        return value || ''
      sessionStorage.removeItem('funnel_q4_email')
    } catch (_) {}
    return ''
  })
  // Wann zeigt das E-Mail-Feld Feedback?
  //   'NONE'     kein Feedback (Start, geleertes Feld, Korrektur nach REQUIRED)
  //   'REQUIRED' Submit-Versuch ohne Eingabe
  //   'LIVE'     mit Inhalt verlassen bzw. abgeschickt → Format/Freemail live spiegeln
  const [emailFeedback, setEmailFeedback] = useState('NONE')
  const emailInputRef = useRef(null)
  // Einmal zentral trimmen: Anzeige und Submit müssen denselben Wert bewerten,
  // sonst zeigt ein eingefügtes ' max@firma.de ' grün an und blockt still.
  const emailValue = email.trim()
  // Eine Quelle für "warum blockt diese Adresse": Anzeige, Tracking und Submit
  // lesen dieselbe Klassifizierung, statt sie je einzeln herzuleiten.
  const blockReason = useMemo(
    () => (emailValue ? classifyEmailBlock(emailValue) : null),
    [emailValue]
  )
  // Freemail, Freemail-Typo und Studenten-Mail sind derselbe Fall: gültige
  // Adresse, falsche Zielgruppe → Talent-Weiche statt Fehlermeldung.
  const showTalentNotice =
    emailFeedback === 'LIVE' &&
    !!blockReason &&
    blockReason !== 'INVALID_FORMAT'

  useEffect(() => {
    try {
      // Nur zulässige Business-Mails vorbelegen — kein halbfertiger Tippversuch
      // und keine bereits abgelehnte Freemail-Adresse.
      if (emailValue && !blockReason) {
        sessionStorage.setItem(
          'funnel_q4_email',
          JSON.stringify({
            value: emailValue,
            expiresAt: Date.now() + 5 * 60 * 1000
          })
        )
      }
    } catch (_) {}
  }, [emailValue, blockReason])
  const [answers, setAnswers] = useState({
    jobTypes: [],
    volume: null,
    hiringPlan: null,
    ats: null
  })
  const [brandData, setBrandData] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [creatingPhase, setCreatingPhase] = useState(0)
  // Der Done-CTA öffnet /login?autologin=1 → Auth.signIn. Das setzt voraus, dass
  // der Cognito-User existiert; angelegt wird er asynchron vom Backend-Stream-
  // Handler beim RECEIVED-Übergang (createTrialPartner). Feuert der Autologin zu
  // früh, wirft Cognito UserNotFoundException — ohne Retry im Login-Pfad.
  //
  // Deshalb wird gepollt statt geraten: derselbe Handler schreibt partner_id in
  // denselben Form-Record zurück (FUNNEL_TRACKING.md) — das ist das Ready-Signal.
  // DONE_DELAY_SECONDS ist nur noch die Obergrenze, falls das Signal ausbleibt
  // (z.B. createTrialPartner geskippt, weil die Domain schon einen Partner hat).
  const DONE_DELAY_SECONDS = 15
  const READY_POLL_INTERVAL_MS = 1500
  // Deckt DONE_DELAY_SECONDS ab; danach gibt der Countdown ohnehin frei. Hart
  // begrenzt, weil query() bei Fehlern notifyBugsnag ruft — eine unbegrenzte
  // Schleife auf einem kaputten Record wäre eine Bugsnag-Flut.
  const READY_POLL_MAX_ROUNDS = 10
  const [doneCountdown, setDoneCountdown] = useState(DONE_DELAY_SECONDS)
  // Ready-Signal vom Backend da → CTA sofort frei, ohne auf den Countdown zu warten.
  const [accountReady, setAccountReady] = useState(false)
  const funnelFormIdRef = useRef(null)
  const refLinkRef = useRef('')
  // Gesammelte geblockte E-Mail-Versuche dieser Session (Format/Freemail/Student).
  // Wird ins data-Feld geschrieben, damit wir sehen, woran Abbrecher scheitern.
  const blockedAttemptsRef = useRef([])
  // Aufruf-Kontext: iframe vs. Direktlink + aufrufende Seite (referrer) + optionale
  // source-Markierung aus der URL. Beim Mount einmal erfasst, in jedes data geschrieben.
  const funnelContextRef = useRef({})
  // Experiment-Zuteilung, eingefroren beim Mount (pages/funnel.js mountet erst,
  // wenn die Flags da sind). Landet als data.visitor_id und data.experiments
  // an jedem Form-Write, darueber verknuepft der experimentEvaluator Lead und
  // Variante. Varianten im Funnel immer hieraus lesen, nicht live per
  // useVariant, sonst kann ein spaet geladenes Flag mitten im Funnel umspringen.
  const experimentDataRef = useRef(null)
  // Exposures im selben Moment aus denselben Flags eingefroren, damit die
  // gemeldete Variante immer die gezeigte ist (siehe freezeSurface)
  const frozenExposuresRef = useRef(null)
  if (!experimentDataRef.current) {
    const ctx = buildContext()
    experimentDataRef.current = {
      visitor_id: ctx.visitorId,
      experiments: activeAssignments(ctx)
    }
    frozenExposuresRef.current = freezeSurface('funnel', ctx)
  }
  // Zeitstempel je erreichtem Step (data.step_timestamps) — macht Zeit-pro-Frage
  // auswertbar; last_activity_time wird ja je Schritt überschrieben.
  const stepTimesRef = useRef({})

  // Alle Funnel-Events tragen die form_id, damit Event-Stream (Firebase) und
  // DB-Record joinbar sind. Beim Intro-Event ist sie noch null (createForm läuft).
  const track = (eventName, params = {}) =>
    trackEvent(eventName, { form_id: funnelFormIdRef.current, ...params })

  // B2C-Hinweis erscheint jetzt konditional (erst bei erkannter Freemail-/
  // Studenten-Adresse) — deshalb erst bei tatsächlicher Sichtbarkeit erfassen,
  // nicht mehr beim Mount. Ref-Guard: eine Impression pro Session, sonst feuert
  // jeder Tastendruck im LIVE-Zustand neu.
  const talentNoticeShownRef = useRef(false)
  useEffect(() => {
    if (!showTalentNotice || talentNoticeShownRef.current) return
    talentNoticeShownRef.current = true
    track(trackEvents.FUNNEL_TALENT_HINT_SHOWN, {
      source: 'funnel_email',
      reason: blockReason,
      domain: emailValue.split('@')[1]?.toLowerCase() || ''
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTalentNotice])

  // Mount: cache ref link + create funnel form item
  useEffect(() => {
    refLinkRef.current =
      typeof window !== 'undefined'
        ? window.location.search.replace(/^\?/, '')
        : ''
    // Aufruf-Kontext einmalig erfassen — automatisch, ohne dass die aufrufende
    // Seite etwas mitgeben muss. referrer = Parent-Seite (im iframe) bzw.
    // verlinkende Seite (Direktlink). source/utm_source nur falls vorhanden.
    // Laufende Session aus localStorage (überlebt Modal-Reopen/Remount).
    const existing = readFunnelSession()
    const sessionId = existing?.session_id || genId()
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      funnelContextRef.current = {
        embedded: window.self !== window.top,
        referrer: document.referrer || null,
        source: params.get('source') || params.get('utm_source') || null,
        // Device-Split: bricht die E-Mail-Wand mobil häufiger?
        is_mobile: window.innerWidth < 768,
        viewport_w: window.innerWidth,
        // Stabil über Reopens derselben Session → Analyse zählt distinct sessions.
        session_id: sessionId
      }
    }
    // Bestehendes Item wiederverwenden → kein zweites createForm bei Remount/Reopen.
    if (existing?.form_id) {
      funnelFormIdRef.current = existing.form_id
      console.warn('[Funnel] reusing form from session', existing.form_id)
    }
    let cancelled = false
    ;(async () => {
      track(trackEvents.FUNNEL_Q4_INTRO_VIEWED, {
        ref_link: refLinkRef.current,
        ...funnelContextRef.current
      })
      if (funnelFormIdRef.current) {
        exposeFrozen(frozenExposuresRef.current, {
          formId: funnelFormIdRef.current
        })
        return // Reuse: nichts Neues anlegen
      }
      try {
        const res = await mutation({
          mutation: 'createForm',
          input: {
            form_source: 'FUNNEL_LEAD_QUALIFICATION',
            status: 'IN_PROGRESS',
            ref_link: refLinkRef.current,
            // Kontext schon beim Anlegen sichern, damit auch reine Bounces
            // (nie was getippt) die Quelle tragen.
            data: JSON.stringify({
              funnel_context: funnelContextRef.current,
              ...experimentDataRef.current
            }),
            lead_progress: 0,
            last_activity: 'INTRO_VIEWED',
            last_activity_time: new Date().toISOString(),
            lastUpdatedBy: 'AWS_IAM'
          },
          authMode: 'AWS_IAM'
        })
        if (cancelled) return
        const id = res?.id
        exposeFrozen(frozenExposuresRef.current, { formId: id })
        if (id) {
          funnelFormIdRef.current = id
          // Session merken → Remounts/Reopens innerhalb 30 Min legen nicht neu an.
          writeFunnelSession({
            form_id: id,
            session_id: sessionId,
            created_at: Date.now(),
            completed: false
          })
          console.warn('[Funnel] form created', { formId: id })
          if (typeof window !== 'undefined' && window.history?.replaceState) {
            try {
              const url = new URL(window.location.href)
              url.searchParams.set('form_id', id)
              window.history.replaceState({}, '', url.toString())
            } catch (_) {
              /* non-blocking */
            }
          }
        } else {
          console.warn('[Funnel] createForm returned no id', { res })
        }
      } catch (e) {
        console.error('[Funnel] createForm failed', e)
        // Auch ohne Lead zaehlt der Besucher als exponiert, sonst fehlt er im Nenner
        exposeFrozen(frozenExposuresRef.current)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Per-step tracking + form progress update
  useEffect(() => {
    const id = funnelFormIdRef.current
    const isDone = step === STEPS.DONE
    const eventName = isDone
      ? trackEvents.FUNNEL_Q4_RESULT_VIEWED
      : trackEvents.FUNNEL_Q4_STEP_VIEWED
    track(eventName, { step })
    stepTimesRef.current[`STEP_${step}`] = new Date().toISOString()
    if (!id) return
    const lead_progress = Math.min(100, Math.round((step / STEPS.DONE) * 100))
    const last_activity = `STEP_${step}_VIEWED`
    console.warn('[Funnel] step update', {
      formId: id,
      step,
      lead_progress,
      last_activity
    })
    mutation({
      mutation: 'updateForm',
      input: {
        id,
        lead_progress,
        last_activity,
        last_activity_time: new Date().toISOString(),
        lastUpdatedBy: 'AWS_IAM'
      },
      authMode: 'AWS_IAM'
    }).catch((e) => {
      console.error('[Funnel] step updateForm failed', {
        formId: id,
        step,
        error: e
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  // Aktuellen (Teil-)Stand der Eingaben als data-Payload bauen. Noch nicht
  // beantwortete Felder bleiben null, damit IN_PROGRESS-Items nichts
  // vortäuschen. Das finale RECEIVED-Item (handleFinalSubmit) überschreibt
  // diesen Snapshot mit der vollständigen, defaulteten Variante.
  const buildFunnelDataPayload = (brand) => {
    const jobTypes = answers.jobTypes || []
    const info = jobTypes.length ? infoDichteFromJobs(jobTypes) : null
    const volume = answers.volume ? VOLUME_MAP[answers.volume] : null
    const atsData = answers.ats
      ? (ATS_MAP[answers.ats] ?? { enum: 'UNBEKANNT' })
      : null
    const groesse =
      brand?.employees != null ? groesseFromEmployees(brand.employees) : null
    // EMAIL nur setzen, wenn die Mail zulässig ist — geblockte Adressen
    // verschmutzen das Lead-Feld nicht, landen in blocked_attempts.
    const acceptedEmail = emailValue && !blockReason ? emailValue : null
    return {
      EMAIL: acceptedEmail,
      funnel_context: funnelContextRef.current,
      ...experimentDataRef.current,
      blocked_attempts: blockedAttemptsRef.current,
      step_timestamps: stepTimesRef.current,
      quali_info_dichte: info?.enum ?? null,
      quali_hiring_volume: volume?.enum ?? null,
      quali_anzahl_stellen: HIRING_PLAN_MAP[answers.hiringPlan] ?? null,
      quali_ats: atsData?.enum ?? null,
      quali_unternehmensgroesse: groesse?.enum ?? null,
      quali_stellentyp_auswahl_raw: jobTypes.join(';'),
      brand_name: brand?.name ?? null,
      brand_employees: brand?.employees ?? null,
      ref_link: refLinkRef.current,
      language: i18n.language
    }
  }

  // Schreibt den aktuellen Stand (E-Mail + bisherige Antworten + Brand) sofort
  // ins data-Feld. Fire-and-forget; AppSync updateForm setzt nur die
  // übergebenen Felder, kollidiert also nicht mit dem [step]-Update.
  const persistFunnelData = (extra = {}) => {
    const id = funnelFormIdRef.current
    if (!id) return
    mutation({
      mutation: 'updateForm',
      input: {
        id,
        data: JSON.stringify(buildFunnelDataPayload(brandData)),
        last_activity_time: new Date().toISOString(),
        lastUpdatedBy: 'AWS_IAM',
        ...extra
      },
      authMode: 'AWS_IAM'
    }).catch((e) =>
      console.error('[Funnel] persistFunnelData failed', {
        formId: id,
        error: e
      })
    )
  }

  // Jede Antwort sofort persistieren (nicht erst beim finalen Submit).
  useEffect(() => {
    const hasAnswer =
      answers.jobTypes.length > 0 ||
      answers.volume ||
      answers.hiringPlan ||
      answers.ats
    if (!hasAnswer) return
    persistFunnelData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers])

  // Brand-Enrichment (Name/Mitarbeiterzahl/Unternehmensgröße) sofort sichern,
  // sobald Brandfetch aufgelöst hat.
  useEffect(() => {
    if (!brandData) return
    persistFunnelData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandData])

  // Creating-step animation: phase 0 (1.6s) → phase 1 (handoff)
  useEffect(() => {
    if (step !== STEPS.CREATING) return
    setCreatingPhase(0)
    const t1 = setTimeout(() => setCreatingPhase(1), 1600)
    return () => clearTimeout(t1)
  }, [step])

  // Building-strip visibility: from creating (after 1.6s) through Q4
  const showBuildingStrip = useMemo(() => {
    if (step === STEPS.CREATING) return creatingPhase >= 1
    return step >= STEPS.Q1 && step <= STEPS.Q4
  }, [step, creatingPhase])

  // Lock body scroll while modal is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Auto-advance for single-select Q2, Q3, Q4
  useEffect(() => {
    if (step === STEPS.Q2 && answers.volume) {
      const t1 = setTimeout(() => setStep(STEPS.Q3), 450)
      return () => clearTimeout(t1)
    }
    if (step === STEPS.Q3 && answers.hiringPlan) {
      const t1 = setTimeout(() => setStep(STEPS.Q4), 450)
      return () => clearTimeout(t1)
    }
    // Q4 → submit (handled by submit handler, triggered by footer button)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.volume, answers.hiringPlan])

  // Done-Countdown: tickt jede Sekunde runter sobald der User den Done-Step
  // sieht. Treibt die Scoring-Animation und ist der Fallback für die CTA-Freigabe,
  // wenn das Ready-Signal ausbleibt — die Freigabe selbst hängt an ctaUnlocked.
  useEffect(() => {
    if (step !== STEPS.DONE) {
      setDoneCountdown(DONE_DELAY_SECONDS)
      setAccountReady(false)
      return
    }
    setDoneCountdown(DONE_DELAY_SECONDS)
    const interval = setInterval(() => {
      setDoneCountdown((s) => {
        if (s <= 1) {
          clearInterval(interval)
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [step])

  // Ready-Polling: fragt den Form-Record ab, bis das Backend partner_id
  // zurückgeschrieben hat (= Trial-Account angelegt, Autologin wird klappen).
  useEffect(() => {
    if (step !== STEPS.DONE) return
    const id = funnelFormIdRef.current
    // Ohne Form-ID (createForm gescheitert) gibt es nichts zu pollen → der
    // Countdown-Fallback bleibt der einzige Pfad, genau wie vorher.
    if (!id) return
    let cancelled = false
    let rounds = 0
    let errors = 0
    const interval = setInterval(async () => {
      if (cancelled) return
      if (++rounds > READY_POLL_MAX_ROUNDS) {
        clearInterval(interval)
        return
      }
      try {
        const form = await query({
          query: 'getForm',
          variables: { id },
          authMode: 'AWS_IAM'
        })
        if (cancelled) return
        errors = 0
        if (form?.partner_id) {
          clearInterval(interval)
          setAccountReady(true)
        }
      } catch (e) {
        // Poll-Fehler sind nicht fatal — der Countdown gibt trotzdem frei.
        // Nach zwei Fehlern in Folge abbrechen: query() meldet jeden Fehler an
        // Bugsnag, und ein strukturelles Problem (Auth, Netz) heilt nicht durch
        // Weiterdrehen — es würde nur zehnmal dasselbe berichten.
        console.warn('[Funnel] ready poll failed', { formId: id, error: e })
        if (++errors >= 2) clearInterval(interval)
      }
    }, READY_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [step])

  // CTA frei, sobald das Backend bestätigt hat — sonst spätestens nach Countdown.
  const ctaUnlocked = accountReady || doneCountdown === 0

  // Freigabe genau einmal messen: macht auswertbar, wie lange die Account-Anlage
  // real dauert und wie oft das Ready-Signal ganz ausbleibt.
  const readyTrackedRef = useRef(false)
  useEffect(() => {
    if (step !== STEPS.DONE) {
      readyTrackedRef.current = false
      return
    }
    if (!ctaUnlocked || readyTrackedRef.current) return
    readyTrackedRef.current = true
    const startedAt = stepTimesRef.current[`STEP_${STEPS.DONE}`]
    track(trackEvents.FUNNEL_Q4_ACCOUNT_READY, {
      source: accountReady ? 'ready_signal' : 'countdown_fallback',
      waited_ms: startedAt ? Date.now() - new Date(startedAt).getTime() : null
    })
  }, [step, ctaUnlocked, accountReady])

  // Kurzer Beat im Endzustand der Scoring-Karte (alle vier Häkchen + voller
  // Score sichtbar), dann erst das Passungsprofil. Ohne das würde die Karte bei
  // früher Freigabe mitten in der Animation verschwinden.
  const [revealProfile, setRevealProfile] = useState(false)
  useEffect(() => {
    if (step !== STEPS.DONE || !ctaUnlocked) {
      setRevealProfile(false)
      return
    }
    const t1 = setTimeout(() => setRevealProfile(true), 600)
    return () => clearTimeout(t1)
  }, [step, ctaUnlocked])

  // Brandfetch in background once email is submitted (during CREATING)
  async function brandfetchAndStore(address) {
    let employees = null
    let brandName = null
    let logoUrl = null
    try {
      const domain = address.split('@')[1]
      logoUrl = `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${domain}&size=128`
      brandName = domain
        .split('.')[0]
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())

      const bfKey = process.env.REACT_APP_BRANDFETCH_KEY
      if (!bfKey) {
        track(trackEvents.FUNNEL_Q4_BRANDFETCH_SKIPPED, { domain })
      } else if (domain) {
        const res = await fetch(
          `https://api.brandfetch.io/v2/brands/${domain}`,
          {
            headers: { Authorization: `Bearer ${bfKey}` }
          }
        )
        if (!res.ok) {
          track(trackEvents.FUNNEL_Q4_BRANDFETCH_NOT_OK, {
            domain,
            status: res.status
          })
        } else {
          const data = await res.json()
          const fmtLogo =
            data.logos?.[0]?.formats?.find(
              (f) => f.format === 'png' || f.format === 'svg'
            )?.src ?? data.logos?.[0]?.formats?.[0]?.src
          if (fmtLogo) logoUrl = fmtLogo
          if (data.name) brandName = data.name
          if (typeof data.company?.employees === 'number') {
            employees = data.company.employees
            track(trackEvents.FUNNEL_Q4_BRANDFETCH_SUCCESS, {
              domain,
              employees
            })
          } else {
            track(trackEvents.FUNNEL_Q4_BRANDFETCH_NO_EMPLOYEES, { domain })
          }
        }
      }
    } catch (e) {
      track(trackEvents.FUNNEL_Q4_BRANDFETCH_ERROR, { message: e?.message })
    }
    const brand = { name: brandName, logoUrl, employees }
    setBrandData(brand)
    return brand
  }

  // ─── E-Mail-Feedback: alle Übergänge auf einem Screen ──────────────────────
  function handleEmailChange(next) {
    setEmail(next)
    // Tippen räumt auf: ein geleertes Feld gilt wieder als unberührt, und nach
    // REQUIRED verschwindet die Meldung sofort, statt zum Format-Fehler zu
    // eskalieren, während der Nutzer noch das erste Zeichen tippt.
    if (!next.trim() || emailFeedback === 'REQUIRED') setEmailFeedback('NONE')
  }

  function handleEmailBlur() {
    // Nur eskalieren, nie zurücknehmen. Ein leeres Feld zu verlassen ist kein
    // Fehler — im iframe auf aivy.app passiert Fokusverlust auch ohne Zutun
    // (Klick ins Host-Dokument blurt das autoFocus-Feld).
    if (emailValue) setEmailFeedback('LIVE')
  }

  // Welcome → Creating handler
  function handleEmailSubmit() {
    // Leeres Feld: Pflichtfeld-Hinweis, nicht als Block-Versuch erfassen.
    if (!emailValue) {
      setEmailFeedback('REQUIRED')
      // Der CTA ist auf WELCOME bewusst nie disabled — ohne Fokus-Rückgabe
      // stünde der Cursor nach dem Fehlversuch im Nirgendwo.
      emailInputRef.current?.focus()
      return
    }
    if (blockReason) {
      setEmailFeedback('LIVE')
      const domain = emailValue.split('@')[1]?.toLowerCase() || ''
      // Versuch akkumulieren (mehrere Anläufe pro Session möglich) und ins Item
      // schreiben, damit wir sehen, woran an der E-Mail-Hürde abgebrochen wird.
      blockedAttemptsRef.current = [
        ...blockedAttemptsRef.current,
        {
          email: emailValue,
          domain,
          reason: blockReason,
          at: new Date().toISOString()
        }
      ]
      track(trackEvents.FUNNEL_Q4_EMAIL_BLOCKED, {
        reason: blockReason,
        domain
      })
      persistFunnelData({ last_activity: 'EMAIL_BLOCKED' })
      return
    }
    track(trackEvents.FUNNEL_Q4_EMAIL_SUBMITTED, {
      domain: emailValue.split('@')[1] || ''
    })
    // E-Mail sofort ins data-Feld schreiben, damit auch Abbrecher (die nie
    // RECEIVED erreichen) per Mail erreichbar sind.
    persistFunnelData()
    // Fire-and-forget brand fetch in background
    brandfetchAndStore(emailValue).catch(() => {})
    if (typeof window !== 'undefined' && window.parent !== window) {
      window.parent.postMessage(
        { type: 'AIVY_FUNNEL_EMAIL_SUBMITTED', email: emailValue },
        '*'
      )
    }
    setStep(STEPS.CREATING)
  }

  // Q4 → DONE: final submit with full quali payload
  async function handleFinalSubmit() {
    if (submitting) return
    if (!answers.ats) return
    setSubmitting(true)
    try {
      // Ensure brand data resolved (or proceed without)
      let brand = brandData
      if (!brand) {
        brand = await Promise.race([
          brandfetchAndStore(emailValue),
          new Promise((resolve) =>
            setTimeout(
              () => resolve({ name: null, logoUrl: null, employees: null }),
              2000
            )
          )
        ])
      }

      const info = infoDichteFromJobs(answers.jobTypes)
      const volume = answers.volume
        ? VOLUME_MAP[answers.volume]
        : { enum: 'LOW', points: 0 }
      const atsData = ATS_MAP[answers.ats] ?? { enum: 'UNBEKANNT', points: 0 }
      const groesse = groesseFromEmployees(brand?.employees)

      const dataPayload = JSON.stringify({
        EMAIL: emailValue,
        funnel_context: funnelContextRef.current,
        ...experimentDataRef.current,
        blocked_attempts: blockedAttemptsRef.current,
        step_timestamps: stepTimesRef.current,
        quali_info_dichte: info.enum,
        quali_hiring_volume: volume.enum,
        quali_anzahl_stellen: HIRING_PLAN_MAP[answers.hiringPlan] ?? null,
        quali_ats: atsData.enum,
        quali_unternehmensgroesse: groesse.enum,
        quali_stellentyp_auswahl_raw: answers.jobTypes.join(';'),
        brand_name: brand?.name ?? null,
        brand_employees: brand?.employees ?? null,
        ref_link: refLinkRef.current,
        language: i18n.language
      })
      const submitInput = {
        status: 'RECEIVED',
        lead_progress: 100,
        last_activity: 'QUALIFICATION_SUBMITTED',
        last_activity_time: new Date().toISOString(),
        data: dataPayload,
        lastUpdatedBy: 'AWS_IAM'
      }
      try {
        const existingId = funnelFormIdRef.current
        if (existingId) {
          // Standard-Pfad: das vom Intro angelegte Item auf RECEIVED stempeln
          console.warn('[Funnel] submit → updateForm', {
            formId: existingId,
            status: 'RECEIVED'
          })
          await mutation({
            mutation: 'updateForm',
            input: { id: existingId, ...submitInput },
            authMode: 'AWS_IAM'
          })
          console.warn('[Funnel] submit complete', { formId: existingId })
        } else {
          // Fallback: Intro-Insert gescheitert → direkt anlegen mit RECEIVED
          console.warn('[Funnel] submit fallback → createForm (no intro id)')
          const res = await mutation({
            mutation: 'createForm',
            input: {
              ...submitInput,
              form_source: 'FUNNEL_LEAD_QUALIFICATION',
              ref_link: refLinkRef.current
            },
            authMode: 'AWS_IAM'
          })
          console.warn('[Funnel] submit complete (fallback)', {
            formId: res?.id
          })
        }
      } catch (e) {
        console.error('[Funnel] submit failed', e)
      }
      // Session als abgeschlossen markieren → ein späterer (echter) Neustart
      // legt sauber ein neues Item an, statt dieses RECEIVED wiederzuverwenden.
      writeFunnelSession({ completed: true })
    } catch (e) {
      console.error('[Funnel] submit failed', e)
    } finally {
      setSubmitting(false)
      setStep(STEPS.DONE)
      if (typeof window !== 'undefined' && window.parent) {
        // Score nochmal aus Antworten + brandData berechnen damit das
        // Landing-Ad-Conversion-Tracking ihn mitlesen kann.
        const score = calcLeadScore({
          volume: answers.volume
            ? VOLUME_MAP[answers.volume]
            : { enum: 'LOW', points: 0 },
          info: infoDichteFromJobs(answers.jobTypes),
          ats: answers.ats
            ? ATS_MAP[answers.ats]
            : { enum: 'UNBEKANNT', points: 0 },
          groesse: groesseFromEmployees(brandData?.employees)
        })
        window.parent.postMessage(
          { type: 'AIVY_FUNNEL_COMPLETE', email: emailValue, score },
          '*'
        )
      }
    }
  }

  function next() {
    if (step === STEPS.WELCOME) return handleEmailSubmit()
    if (step === STEPS.CREATING) return setStep(STEPS.Q1)
    if (step === STEPS.Q1) {
      if (typeof window !== 'undefined' && window.parent !== window) {
        window.parent.postMessage({ type: 'AIVY_FUNNEL_Q1_COMPLETED' }, '*')
      }
      return setStep(STEPS.Q2)
    }
    if (step === STEPS.Q4) return handleFinalSubmit()
  }
  function back() {
    if (step <= STEPS.WELCOME) return
    if (step === STEPS.Q1) return setStep(STEPS.WELCOME)
    track(trackEvents.FUNNEL_Q4_BACK_CLICKED, { from_step: step })
    setStep((s) => Math.max(STEPS.WELCOME, s - 1))
  }

  const canProceed = (() => {
    if (step === STEPS.WELCOME)
      return isValidEmailFormat(emailValue) && !isPrivateEmail(emailValue)
    if (step === STEPS.Q1) return answers.jobTypes.length > 0
    if (step === STEPS.Q4) return !!answers.ats && !submitting
    return true
  })()

  const showBackButton =
    step > STEPS.WELCOME && step !== STEPS.CREATING && step !== STEPS.DONE

  const nextLabel = (() => {
    if (step === STEPS.WELCOME) return t('funnel_q4.welcome.cta')
    if (step === STEPS.Q4)
      return submitting ? t('funnel_q4.submitting') : t('funnel_q4.submit')
    return t('funnel_q4.next').replace(/[→\s]+$/, '')
  })()

  // Tastatur-Bedienung für Q1–Q4: Ziffern 1–N wählen die Option, Enter geht weiter.
  // (Auf WELCOME ist Enter bereits am Input belegt → Handler nur in den Fragen.)
  useEffect(() => {
    if (step < STEPS.Q1 || step > STEPS.Q4) return
    const idsByStep = {
      [STEPS.Q1]: JOB_TYPES,
      [STEPS.Q2]: VOLUME_IDS,
      [STEPS.Q3]: HIRING_IDS,
      [STEPS.Q4]: ATS_IDS
    }
    const onKey = (e) => {
      const tag = e.target?.tagName
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      )
        return
      if (e.key === 'Enter') {
        if (canProceed) {
          e.preventDefault()
          next()
        }
        return
      }
      const ids = idsByStep[step]
      const n = parseInt(e.key, 10)
      if (!Number.isInteger(n) || n < 1 || n > ids.length) return
      e.preventDefault()
      const id = ids[n - 1]
      if (step === STEPS.Q1) {
        setAnswers((a) => ({
          ...a,
          jobTypes: a.jobTypes.includes(id)
            ? a.jobTypes.filter((x) => x !== id)
            : [...a.jobTypes, id]
        }))
      } else if (step === STEPS.Q2) {
        setAnswers((a) => ({ ...a, volume: id }))
      } else if (step === STEPS.Q3) {
        setAnswers((a) => ({ ...a, hiringPlan: id }))
      } else if (step === STEPS.Q4) {
        setAnswers((a) => ({ ...a, ats: id }))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, canProceed])

  const [confirmingClose, setConfirmingClose] = useState(false)

  function requestClose() {
    if (!onClose) return
    if (step === STEPS.DONE) {
      onClose()
      return
    }
    setConfirmingClose(true)
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) requestClose()
  }

  return (
    <div className='lm-modal-backdrop' onClick={handleBackdropClick}>
      <div className='lm-modal' role='dialog' aria-modal='true'>
        <div className='lm-modal-header'>
          <div className='lm-modal-brand'>
            <img src={aivyLogo} alt='Aivy' />
          </div>
          {onClose && (
            <button
              type='button'
              className='lm-modal-close'
              onClick={requestClose}
              aria-label='Schließen'
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {confirmingClose && (
          <div
            className='lm-modal-backdrop'
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 10000
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfirmingClose(false)
            }}
          >
            <div
              role='alertdialog'
              aria-modal='true'
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'var(--white, #fff)',
                borderRadius: 12,
                padding: '24px 24px 20px',
                width: 'min(420px, 90%)',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
              }}
            >
              {t('funnel_q4.close_confirm.title') && (
                <h3
                  style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}
                >
                  {t('funnel_q4.close_confirm.title')}
                </h3>
              )}
              <p
                style={{
                  margin: '0 0 20px',
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: 'var(--lm-dark-black, #182033)'
                }}
              >
                {t('funnel_q4.close_confirm.body')}
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap'
                }}
              >
                <button
                  type='button'
                  className='lm-btn lm-btn-ghost'
                  onClick={() => {
                    setConfirmingClose(false)
                    onClose && onClose()
                  }}
                >
                  {t('funnel_q4.close_confirm.leave')}
                </button>
                <button
                  type='button'
                  className='lm-btn lm-btn-primary'
                  onClick={() => setConfirmingClose(false)}
                >
                  {t('funnel_q4.close_confirm.continue')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showBuildingStrip && <BuildingStrip t={t} step={step} />}

        <ProgressBar step={step} t={t} />

        <div className='lm-modal-body'>
          {step === STEPS.WELCOME && (
            <WelcomeStep
              email={email}
              emailFeedback={emailFeedback}
              blockReason={blockReason}
              showTalentNotice={showTalentNotice}
              onEmailChange={handleEmailChange}
              onEmailBlur={handleEmailBlur}
              inputRef={emailInputRef}
              onSubmit={handleEmailSubmit}
              onTalentHintClick={() =>
                // reason + domain mitgeben, damit Impression → Klick joinbar ist
                track(trackEvents.PRESSED_GO_TO_TALENTS, {
                  source: 'funnel_email',
                  reason: blockReason,
                  domain: emailValue.split('@')[1]?.toLowerCase() || ''
                })
              }
              t={t}
              transComponents={transComponents}
            />
          )}
          {step === STEPS.CREATING && (
            <CreatingStep phase={creatingPhase} t={t} />
          )}
          {step >= STEPS.Q1 && step <= STEPS.Q4 && (
            <QuestionStep
              step={step}
              answers={answers}
              setAnswers={setAnswers}
              t={t}
              transComponents={transComponents}
            />
          )}
          {step === STEPS.DONE && (
            <DoneStep
              answers={answers}
              brandData={brandData}
              t={t}
              doneCountdown={doneCountdown}
              totalSeconds={DONE_DELAY_SECONDS}
              ctaUnlocked={ctaUnlocked}
              revealProfile={revealProfile}
            />
          )}
        </div>

        {step === STEPS.CREATING ? (
          creatingPhase >= 1 && (
            <div className='lm-modal-footer'>
              <span />
              <button type='button' className='lm-next-btn' onClick={next}>
                {t('funnel_q4.creating.continue')} <ArrowRight />
              </button>
            </div>
          )
        ) : step === STEPS.DONE ? (
          <div className='lm-modal-footer' style={{ justifyContent: 'center' }}>
            <button
              type='button'
              className='lm-next-btn'
              disabled={!ctaUnlocked}
              onClick={() => {
                if (!ctaUnlocked) return
                const url = `/login?email=${encodeURIComponent(emailValue)}&autologin=1`
                track(trackEvents.FUNNEL_Q4_CTA_CLICKED, {
                  flag: 'goto_dashboard'
                })
                // CTA-Klick im Form festhalten: unterscheidet bei der
                // Done-zu-Login-Lücke "nie geklickt" von "geklickt, aber
                // Login gescheitert" (z.B. OTP). Status bleibt RECEIVED,
                // kein Stream-Trigger (kein Status-/Stage-Übergang).
                const formId = funnelFormIdRef.current
                if (formId) {
                  mutation({
                    mutation: 'updateForm',
                    input: {
                      id: formId,
                      last_activity: 'DASHBOARD_CTA_CLICKED',
                      last_activity_time: new Date().toISOString(),
                      lastUpdatedBy: 'AWS_IAM'
                    },
                    authMode: 'AWS_IAM'
                  }).catch(() => {})
                }
                window.open(url, '_blank', 'noopener')
                // Tell landing parent (if iframed) to close its modal
                if (typeof window !== 'undefined' && window.parent !== window) {
                  window.parent.postMessage(
                    { type: 'AIVY_FUNNEL_OPENED_LOGIN', email: emailValue },
                    '*'
                  )
                }
                if (onClose) onClose()
              }}
            >
              {ctaUnlocked
                ? t('funnel_q4.done.close')
                : t('funnel_q4.done.close_countdown', {
                    seconds: doneCountdown
                  })}
            </button>
          </div>
        ) : (
          (() => {
            const autoAdvance = step === STEPS.Q2 || step === STEPS.Q3
            return (
              <div
                className={`lm-modal-footer ${autoAdvance ? 'lm-modal-footer--compact' : ''}`}
              >
                <button
                  type='button'
                  className='lm-back-btn'
                  onClick={back}
                  disabled={!showBackButton}
                >
                  <ArrowLeft /> {t('funnel_q4.back').replace(/^[←\s]+/, '')}
                </button>
                {step === STEPS.WELCOME && (
                  <div className='lm-foot-meta'>
                    <span className='lm-foot-item'>
                      <ClockIcon aria-hidden='true' />
                      {t('funnel_q4.intro.trust_time')}
                    </span>
                    <span className='lm-foot-dot' />
                    <span className='lm-foot-item lm-foot-item--rating'>
                      <StarIcon aria-hidden='true' />
                      {t('funnel_q4.intro.trust_rating')}
                    </span>
                    <span className='lm-foot-dot' />
                    <span className='lm-foot-item'>
                      <LockClosedIcon aria-hidden='true' />
                      {t('funnel_q4.intro.trust_gdpr')}
                    </span>
                  </div>
                )}
                {!autoAdvance && (
                  <button
                    type='button'
                    className={`lm-next-btn ${step === STEPS.WELCOME ? 'green' : ''}`}
                    onClick={next}
                    // WELCOME bleibt klickbar, damit geblockte Mails erfasst
                    // werden (handleEmailSubmit zeigt den Fehler, schaltet nicht weiter).
                    disabled={step === STEPS.WELCOME ? false : !canProceed}
                  >
                    {nextLabel} <ArrowRight />
                  </button>
                )}
              </div>
            )
          })()
        )}
      </div>

      {/* Eine Instanz für alle Frage-Anchors, absichtlich außerhalb von
          .lm-modal: der Backdrop ist position:fixed ohne overflow/transform →
          nichts clippt den Tooltip, und er ist kein Sibling der Frage-Inhalte
          (sonst kippen die Abstands-Selektoren beim Öffnen).
          positionStrategy fixed, damit floating-ui gegen den sichtbaren
          Viewport shiftet statt gegen die (ggf. breitere) Dokument-Rect. */}
      <Tooltip
        id='funnel-q-why'
        className='lm-why-tooltip'
        offset={10}
        place={isNarrow ? 'top' : 'right'}
        positionStrategy='fixed'
        openOnClick={isNarrow}
        // globalCloseEvents ersetzt die Defaults komplett (kein Merge) und der
        // Key heißt clickOutsideAnchor. Fehlt er, gibt es im openOnClick-Modus
        // gar keinen Close-Pfad mehr: die Library schaltet dort mouseleave/blur
        // ab, und Escape/Scroll helfen auf Touch nicht.
        globalCloseEvents={{
          escape: true,
          scroll: true,
          clickOutsideAnchor: true
        }}
        // clickOutsideAnchor ignoriert Taps auf die Anchors selbst — für den
        // zweiten Tap aufs Icon (= schließen) braucht es closeEvents.click.
        // Auf Desktop bleibt es beim Hover-Default.
        closeEvents={
          isNarrow ? { click: true } : { mouseleave: true, blur: true }
        }
      />
    </div>
  )
}
